import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

// styles
import './style.scss';

// antd component
import { Form, Button } from 'antd';

// assets
import CustomIcons from 'components/CustomIcons';

// components
import PreviewMedia from 'components/Forms/Fields/PreviewMedia';
import CustomSelect from 'components/Forms/Fields/CustomSelect';

// utils
// import { sortByKey } from 'utils/helpers/helper.general';
import {
  getAbsenceTypeLabel,
  getUndertakeLabel,
  getAbsenceCoursesTime,
  getStatusesList,
} from 'utils/helpers/helper.generateData';

// global user
import { useGetLocalUser } from 'engine/Contexts/User.context';

function ResumeForm({
  formData,
  isEdit,
  // actions
  nextStep,
  prevStep,
  goToStep,
  result,
  // default
  className,
}) {
  const childClassName = classNames('form-absence', className);
  const timeFormat = 'HH:mm';
  const dateFormat = 'DD.MM.YYYY';

  const localUser = useGetLocalUser()();

  const [isDisabled, setIsDisabled] = useState(false);
  const [parentCanEdit, setParentCanEdit] = useState(
    isEdit && localUser?.role == 'parent',
  );
  const [teacherCanEdit, setTeacherCanEdit] = useState(
    isEdit && localUser?.role == 'teacher',
  );

  const [absenceStatus, setAbsenceStatus] = useState(
    formData?.status || 'pending',
  );

  const [AbsFormResume] = Form.useForm();

  /*
   * Check current form data from parent
   * Set old data to the form
   */
  useEffect(() => {
    if (formData) {
      const absStatus = formData?.status || 'pending';
      AbsFormResume.setFieldsValue(formData);
      AbsFormResume.setFieldsValue({
        status: absStatus,
      });
      setAbsenceStatus(absStatus);
      setParentCanEdit(prevStatus => {
        if (
          (!!isEdit &&
            formData?.status == 'refused' &&
            localUser?.role == 'parent')
        ) {
          return false;
        } else if ( !isEdit && localUser?.role == 'parent' ) {
          return true;
        } else {
          return prevStatus;
        }
      });
      setTeacherCanEdit(prevStatus => {
        if (
          localUser?.role != 'teacher' 
        ) {
          return false;
        } else if ( !isEdit && localUser?.role == 'teacher' ) {
          return true;
        } else {
          return prevStatus;
        }
      });
      setIsDisabled(false);
    }
  }, [formData]);

  const onFinish = values => {
    result(values);
    nextStep !== undefined && nextStep();
    AbsFormResume.resetFields();
  };

  const generateDuration = (data) => {
    if (data) {
      if (data?.duration == 'more') {
        return (
          <>
            <div className="resume-fields__row">
              <div className="column helper">Du</div>
              <div className="column">
                {moment(data?.date_from).format(dateFormat)}
              </div>
              <div className="column">
                {moment(data?.time_from).format(timeFormat)}
              </div>
            </div>
            <div className="resume-fields__row">
              <div className="column helper">Au</div>
              <div className="column">
                {moment(data?.date_to).format(dateFormat)}
              </div>
              <div className="column">
                {moment(data?.time_to).format(timeFormat)}
              </div>
            </div>
          </>
        );
      } else if (data?.duration == 'day') {
        let timeRange = getAbsenceCoursesTime(data);
        const lastIndex = timeRange.length - 1;

        return (
          <>
            <div className="resume-fields__row">
              <div className="column helper">Du</div>
              <div className="column">
                {moment(data?.date).format(dateFormat)}
              </div>
              <div className="column">
                {timeRange.length > 0 ? timeRange[0]?.start : '--'}
              </div>
            </div>
            <div className="resume-fields__row">
              <div className="column helper">Au</div>
              <div className="column">
                {moment(data?.date).format(dateFormat)}
              </div>
              <div className="column">
                {timeRange.length > 0 ? timeRange[lastIndex]?.end : '--'}
              </div>
            </div>
          </>
        );
      }
    }
  };

  const goMediaStep = () => {
    const hasMedia =
      !formData?.media || (formData?.media && formData?.media.length <= 0);
    const fomrMediaStep = isEdit && localUser?.role == 'parent' ? 1 : 4;
    const previewMediaStep = isEdit && localUser?.role == 'parent' ? 2 : 5;
    goToStep(hasMedia ? fomrMediaStep : previewMediaStep);
  };

  const goToPrev = () => {
    if (isEdit && localUser?.role == 'parent') {
      goMediaStep();
    } else {
      prevStep !== undefined && prevStep();
    }
  };

  return (
    <div className={childClassName}>
      <Form
        form={AbsFormResume}
        className="form-absence__form"
        name="form_absence_resume"
        initialValues=""
        onFinish={onFinish}
        onValuesChange={values => {
          setIsDisabled(false);
          if (values?.status) {
            setAbsenceStatus(values?.status);
          }
        }}
      >
        {/* Motif de l’absence */}
        <div className="resume-section">
          <h4 className="resume-title">Motif de l’absence</h4>
          {teacherCanEdit && (
            <CustomIcons
              type="edit"
              className="resume-edit"
              onClick={() => goToStep(2)}
            />
          )}
          <div className="resume-fields">
            {`${getAbsenceTypeLabel(formData?.type)} (${formData?.reason})`}
          </div>
        </div>

        {/* Durée de l'absence */}
        <div className="resume-section">
          <h4 className="resume-title">Durée de l'absence</h4>
          {teacherCanEdit && (
            <CustomIcons
              type="edit"
              className="resume-edit"
              onClick={() => goToStep(3)}
            />
          )}
          <div className="resume-fields">
            {generateDuration(formData)}
          </div>
        </div>

        {/* Justificatif */}
        <div className="resume-section">
          <h4 className="resume-title">Justificatif</h4>
          {(parentCanEdit || teacherCanEdit) && (
            <CustomIcons
              type="edit"
              className="resume-edit"
              onClick={goMediaStep}
            />
          )}
          <div className="resume-fields">
            {(!formData?.media ||
              (formData?.media && formData?.media.length <= 0)) &&
              `Je n'ai pas de justificatif`}
            {formData?.media && formData?.media.length > 0 && (
              <PreviewMedia file={formData?.media[0]} showTitle={false} />
            )}
          </div>
        </div>

        {/* Déplacement */}
        <div className="resume-section">
          <h4 className="resume-title">Déplacement</h4>
          {teacherCanEdit && (
            <CustomIcons
              type="edit"
              className="resume-edit"
              onClick={() => goToStep(6)}
            />
          )}
          <div className="resume-fields">
            {getUndertakeLabel(formData?.parents_undertake)}
          </div>
        </div>

        {/* Status */}
        {localUser?.role == 'teacher' && (
          <div className="resume-section">
            <div className="resume-fields">
              <CustomSelect
                className={`accent-label select-status ${absenceStatus}`}
                label={`Status`}
                value={absenceStatus}
                placeholder="Selectionner"
                name={`status`}
                options={getStatusesList(formData?.type)}
              />
            </div>
          </div>
        )}

        {(parentCanEdit || teacherCanEdit) && (
          <div className="absence-actions-wrapper">
            <Button className="btn-previous" onClick={goToPrev}>
              Précédent
            </Button>
            <Button
              className="confirm-btn"
              form="form_absence_resume"
              htmlType="submit"
              disabled={isDisabled}
            >
              Confirmer
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
}

ResumeForm.defaultProps = {};
ResumeForm.propTypes = {
  formData: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
    PropTypes.string.isRequired,
  ]),
  result: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
};

export default memo(ResumeForm);
